import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import type { User, UserManager, UserProfile } from "oidc-client-ts";

import { bindCrossTabSync, bindOidcEvents, emitAuthSignal } from "./authEvents";
import { readOidcEnv } from "./oidcConfig";
import type { OidcEnv } from "./oidcConfig";
import { redirectToInteractiveLogin } from "./interactiveLogin";
import { setLogoutInProgress } from "./redirectGuard";
import { getOidcUserManager } from "./userManagerSingleton";
import { clearPermissionsCache } from "../utils/permissionsCache";
import { clearCurrentUserCache } from "../utils/currentUserCache";
import { setAnalyticsUser, setAnalyticsCustomerType } from "../lib/analytics";

type AuthContextValue = Readonly<{
  isLoading: boolean;
  isAuthenticated: boolean;
  user: User | null;
  profile: UserProfile | null;
  accessToken: string | null;
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  renew: () => Promise<boolean>;
  getUserManager: () => Promise<UserManager>;
  /** Re-read user from storage and update context (used by callback to avoid race). */
  syncUserFromStorage: () => Promise<void>;
}>;

const AuthContext = createContext<AuthContextValue | null>(null);

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function safeStringifyError(error: unknown): string {
  if (error instanceof Error) return error.message;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function isUnrecoverableRenewError(error: unknown): boolean {
  const msg = safeStringifyError(error).toLowerCase();
  // Cognito / OIDC common terminal errors for silent renew/refresh token flows.
  return (
    msg.includes("login_required") ||
    msg.includes("interaction_required") ||
    msg.includes("consent_required") ||
    msg.includes("invalid_grant") ||
    msg.includes("invalid_token") ||
    // Cognito Hosted UI / ALB OIDC can return these when refresh token is no longer valid
    // (global signout, user disabled/deleted, or refresh token expired).
    msg.includes("globally signed out") ||
    msg.includes("access token is expired")
  );
}

async function buildCognitoLogoutUrl(
  userManager: UserManager,
  oidcEnv: OidcEnv
): Promise<string> {
  // Cognito Hosted UI does not always advertise `end_session_endpoint` in discovery.
  // Security: we derive the logout host from the provider's `authorization_endpoint` (discovery),
  // rather than trusting string concatenation on `authority`.
  const metadata = await userManager.metadataService.getMetadata();
  const authorizationEndpoint = metadata.authorization_endpoint;
  if (!authorizationEndpoint) {
    throw new Error("OIDC metadata missing authorization_endpoint");
  }
  const origin = new URL(authorizationEndpoint).origin;
  const logoutUrl = new URL("/logout", origin);
  logoutUrl.searchParams.set("client_id", oidcEnv.clientId);
  logoutUrl.searchParams.set("logout_uri", oidcEnv.postLogoutRedirectUri);
  return logoutUrl.toString();
}

function getCurrentPathWithQuery(): string {
  if (!isBrowser()) return "/";
  return `${window.location.pathname}${window.location.search}`;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);

  const oidcEnvRef = useRef<OidcEnv | null>(null);
  const userManagerRef = useRef<UserManager | null>(null);
  const initPromiseRef = useRef<Promise<UserManager> | null>(null);
  const renewPromiseRef = useRef<Promise<boolean> | null>(null);
  const loginPromiseRef = useRef<Promise<void> | null>(null);
  /** Set when silent renew failed with an unrecoverable error; used to redirect only after token actually expires. */
  const unrecoverableRenewFailedRef = useRef<boolean>(false);

  const updateUser = useCallback((nextUser: User | null) => {
    // When identity changes (or we newly become authenticated), clear caches that
    // embed user/customer context so they refetch for the new session.
    setUser((prev) => {
      const prevSub = prev?.profile?.sub ?? null;
      const nextSub = nextUser?.profile?.sub ?? null;

      const prevAuth = Boolean(prev && !prev.expired);
      const nextAuth = Boolean(nextUser && !nextUser.expired);

      const identityChanged = prevSub !== nextSub;
      const becameAuthenticated = !prevAuth && nextAuth;

      if (identityChanged || becameAuthenticated) {
        clearPermissionsCache();
        clearCurrentUserCache();
      }

      setAnalyticsUser(nextUser?.profile ?? null);
      if (!nextUser) {
        setAnalyticsCustomerType(null);
      } else if (identityChanged || becameAuthenticated) {
        // Wait for CDS customer_type before sending product events.
        setAnalyticsCustomerType(undefined);
      }

      return nextUser;
    });
  }, []);

  const getUserManager = useCallback(async (): Promise<UserManager> => {
    if (!isBrowser()) {
      throw new Error("OIDC UserManager is only available in the browser.");
    }

    if (userManagerRef.current) return userManagerRef.current;
    if (initPromiseRef.current) return initPromiseRef.current;

    initPromiseRef.current = (async () => {
      const oidcEnv = readOidcEnv();
      oidcEnvRef.current = oidcEnv;
      const userManager = getOidcUserManager();
      userManagerRef.current = userManager;

      // Bind OIDC events for this tab.
      const unbindOidc = bindOidcEvents({
        userManager,
        onUserLoaded: (loadedUser) => {
          unrecoverableRenewFailedRef.current = false;
          updateUser(loadedUser);
          // Cross-tab: announce login for deterministic UI updates in other tabs.
          emitAuthSignal(window.localStorage, { type: "login", at: Date.now() });
        },
        onUserUnloaded: () => {
          updateUser(null);
        },
        onSilentRenewError: (error) => {
          // Requirement: only console.error allowed.
          // eslint-disable-next-line no-console
          console.error("OIDC silent renew error:", error);
        },
      });

      // Bind cross-tab sync to react to other tabs updating the OIDC user record.
      const unbindCrossTab = bindCrossTabSync({
        userManager,
        authority: oidcEnv.authority,
        clientId: oidcEnv.clientId,
        onRemoteUserUpdated: (remoteUser) => {
          unrecoverableRenewFailedRef.current = false;
          updateUser(remoteUser);
        },
        onRemoteLogout: () => {
          updateUser(null);
          // Deterministic logout across tabs: ensure we return to app root.
          void router.replace("/");
        },
        onError: (error) => {
          // eslint-disable-next-line no-console
          console.error("OIDC cross-tab sync error:", error);
        },
      });

      // Token lifecycle hooks (per-tab): renew before expiry, and recover on expiry.
      // Only redirect to login when the token has actually expired and renew failed (not when
      // "expiring" renew fails 2 min early), so users are not logged out before token expiry.
      const onExpiring = () => {
        void renew();
      };
      const onExpired = async () => {
        const renewed = await renew();
        if (!renewed && unrecoverableRenewFailedRef.current) {
          try {
            await userManager.removeUser();
            updateUser(null);
          } catch (removeError) {
            // eslint-disable-next-line no-console
            console.error("OIDC removeUser failed after renew error:", removeError);
          }
          void login(getCurrentPathWithQuery());
        }
      };
      userManager.events.addAccessTokenExpiring(onExpiring);
      userManager.events.addAccessTokenExpired(onExpired);

      // Initial user load from localStorage. If the stored user's access token is already
      // expired (e.g. user opened app after hours or days), try silent renew once so the
      // 7-day refresh token can restore the session instead of sending them to login.
      const existingUser = await userManager.getUser();
      if (existingUser?.expired) {
        const renewed = await renew();
        if (renewed) {
          unrecoverableRenewFailedRef.current = false;
          // renew() already called updateUser with the new user
        } else {
          try {
            await userManager.removeUser();
          } catch {
            // ignore
          }
          updateUser(null);
        }
      } else {
        unrecoverableRenewFailedRef.current = false;
        updateUser(existingUser);
      }
      setIsLoading(false);

      // Cleanup when provider unmounts.
      const originalDispose = () => {
        userManager.events.removeAccessTokenExpiring(onExpiring);
        userManager.events.removeAccessTokenExpired(onExpired);
        unbindCrossTab();
        unbindOidc();
      };

      // Attach dispose function without globals.
      (userManager as unknown as { __dispose?: () => void }).__dispose = originalDispose;

      return userManager;
    })();

    return initPromiseRef.current;
  }, [router, updateUser]);

  const login = useCallback(
    async (returnTo?: string): Promise<void> => {
      if (!isBrowser()) return;
      // Prevent multiple concurrent signinRedirect attempts (redirect stampedes).
      if (loginPromiseRef.current) return loginPromiseRef.current;

      loginPromiseRef.current = (async () => {
        await getUserManager(); // ensure init/bindings are established before redirecting
        await redirectToInteractiveLogin(returnTo);
      })().finally(() => {
        loginPromiseRef.current = null;
      });

      return loginPromiseRef.current;
    },
    [getUserManager]
  );

  const renew = useCallback(async (): Promise<boolean> => {
    if (!isBrowser()) return false;

    if (renewPromiseRef.current) return renewPromiseRef.current;

    // Defensive: ensure renew() never throws/rejects (expired event handler awaits it).
    renewPromiseRef.current = (async () => {
      const userManager = await getUserManager();
      const renewedUser = await userManager.signinSilent();
      unrecoverableRenewFailedRef.current = false;
      updateUser(renewedUser);
      return true;
    })()
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("OIDC token renew failed:", error);

        if (isUnrecoverableRenewError(error)) {
          unrecoverableRenewFailedRef.current = true;
          // Do not remove user or redirect here. Let onExpired do it when the token has
          // actually expired, so users are not logged out 2 min before expiry (expiring event).
        }
        return false;
      })
      .finally(() => {
        renewPromiseRef.current = null;
      });

    return renewPromiseRef.current;
  }, [getUserManager, updateUser]);

  const logout = useCallback(async (): Promise<void> => {
    if (!isBrowser()) return;

    // Clear permissions cache immediately so they don't persist after logout
    clearPermissionsCache();
    clearCurrentUserCache();

    // Prevent AuthGuard from seeing isAuthenticated=false and redirecting to login
    // before we complete the Cognito logout redirect (same-tab race).
    setLogoutInProgress(true);

    const userManager = await getUserManager();
    const oidcEnv = oidcEnvRef.current ?? readOidcEnv();

    // Cross-tab deterministic logout: broadcast first, then clear local tokens.
    emitAuthSignal(window.localStorage, { type: "logout", at: Date.now() });

    // Revoke refresh token server-side before clearing local state. Cognito invalidates all
    // access tokens issued from the revoked refresh token. Must happen before removeUser()
    // because removeUser() deletes tokens from localStorage, leaving nothing to revoke.
    // eslint-disable-next-line no-console
    await userManager.revokeTokens(["refresh_token"]).catch((error) => console.error("OIDC token revocation failed:", error));

    try {
      await userManager.removeUser(); // Remove user from localStorage on logging out  and revoking tokens
      await userManager.clearStaleState();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("OIDC local logout cleanup failed:", error);
    } finally {
      updateUser(null);
    }

    try {
      const url = await buildCognitoLogoutUrl(userManager, oidcEnv);
      window.location.assign(url);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("OIDC Cognito logout redirect failed:", error);
      window.location.assign(oidcEnv.postLogoutRedirectUri);
    }
  }, [getUserManager, updateUser]);

  const syncUserFromStorage = useCallback(async (): Promise<void> => {
    if (!isBrowser()) return;
    const userManager = await getUserManager();
    const nextUser = await userManager.getUser();
    updateUser(nextUser);
  }, [getUserManager, updateUser]);

  // Ensure initialization starts as soon as we mount (client-side).
  useEffect(() => {
    if (!isBrowser()) return;
    void getUserManager().catch((error) => {
      // eslint-disable-next-line no-console
      console.error("OIDC initialization failed:", error);
      setIsLoading(false);
    });

    return () => {
      const userManager = userManagerRef.current;
      const dispose = (userManager as unknown as { __dispose?: () => void } | null)?.__dispose;
      dispose?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accessToken = user?.access_token ?? null;
  const profile = user?.profile ?? null;
  const isAuthenticated = Boolean(user && !user.expired);

  const value: AuthContextValue = useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      user,
      profile,
      accessToken,
      login,
      logout,
      renew,
      getUserManager,
      syncUserFromStorage,
    }),
    [accessToken, getUserManager, isAuthenticated, isLoading, login, logout, profile, renew, syncUserFromStorage, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext must be used within AuthProvider");
  return ctx;
}
